import { useContext, useEffect, useState } from 'react';
import { useQuery, UseQueryResult } from 'react-query';

import { ApiContext } from '../Api/Api';
import { URLStateContext } from '../URLState/URLState';
import appendURLSearchParams from '../../utils/appendURLSearchParams';
import getDateRange from '../../utils/time/getTimeRange';

type CrashEventListEntry = {
  current: string | null;
  previous: string | null;
  next: string | null;
};

type HookPayload = {
  firstCrashEventId: string | null;
  isFetched: boolean;
};
type HookResult = Pick<UseQueryResult, 'isFetching'> & HookPayload;

/**
 * A hook that retrieves a list of CrashEvents, for a given Crash (by crashId).
 *
 * @param  {string} crashId
 * @returns HookResult
 */
const useCrashEventList = (crashId: string, pageSize = 1): HookResult => {
  const { get } = useContext(ApiContext);
  const { dateFrom, dateTo, duration } = useContext(URLStateContext);
  const [firstCrashEventId, setFirstCrashEventId] = useState<string | null>(null);
  const { dateFrom: start, dateTo: end } = getDateRange(duration, dateFrom, dateTo);

  const { data, isFetching, isSuccess } = useQuery<{
    ids: CrashEventListEntry[];
    total: number;
  }>(['crashEventList', { crashId, dateFrom, dateTo, get, pageSize }], async () =>
    get(
      `/issues/${crashId}/events?${appendURLSearchParams(
        '',
        ['page_size', String(pageSize)],
        ['start', String(start)],
        ['end', String(end)],
      )}`,
    ),
  );

  /**
   * Use a side-effect to:
   *  - Update the `firstCrashEventId` state, whenever the crashEventList query resolves
   */
  useEffect(() => {
    if (data) {
      const { ids: [{ current = null } = {}] = [] } = data;
      setFirstCrashEventId(current);
    }
  }, [data]);

  return {
    firstCrashEventId,
    isFetched: isSuccess,
    isFetching,
  };
};

export default useCrashEventList;
